import Flickity from 'flickity'
import { parseJSON } from '@/components/helpers/utils'
const defaultOptions = {
  contain: true
}
export class Carousel extends HTMLElement {
  constructor() {
    super()
    this.animationId = null
    this.isPaused = false
    this.smoothScrollSpeed = 0.5
    this.smoothScrollEnabled = false
    this.resumeTimeout = null
    this.lastPosition = 0
    this.isManualNavigation = false
  }

  hideLoader() {
    this.classList.add('carousel-ready')
  }

  connectedCallback() {
    this.style.opacity = '0'
    this.style.visibility = 'hidden'

    const computedHeight = this.getBoundingClientRect().height

    if (computedHeight > 0) {
      this.style.minHeight = `${computedHeight}px`
    }

    const options = this.carouselSettings
    options.cellSelector = '&:not(style)'
    const mergedOptions = { ...defaultOptions, ...options }

    this.smoothScrollEnabled = mergedOptions.smoothScroll === true || mergedOptions.smoothScroll === 'true'
    const smoothScrollSpeed = mergedOptions.smoothScrollSpeed || 0.5
    delete mergedOptions.smoothScroll
    delete mergedOptions.smoothScrollSpeed

    if (mergedOptions.wrapAround === true) {
      delete mergedOptions.contain
    }

    if (this.smoothScrollEnabled) {
      mergedOptions.autoPlay = false
      mergedOptions.freeScroll = false
      this.smoothScrollSpeed = smoothScrollSpeed
    } else if (mergedOptions.autoPlay && mergedOptions.freeScroll === true) {
      mergedOptions.freeScroll = false
    }

    this.initializeFlickity(mergedOptions)
    this.setupImageLoadListeners()
  }

  initializeFlickity(mergedOptions) {
    const images = this.querySelectorAll('img')
    const lazyImages = this.querySelectorAll('img.lazy')

    const checkImageDimensions = (img) => {
      return img.complete && img.naturalWidth > 0 && img.naturalHeight > 0
    }

    const waitAndInitialize = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const containerRect = this.getBoundingClientRect()
          if (containerRect.width > 0 || containerRect.height > 0) {
            this.createFlickity(mergedOptions)
          } else {
            setTimeout(() => {
              if (!this.flickity) {
                this.createFlickity(mergedOptions)
              }
            }, 100)
          }
        })
      })
    }

    if (images.length === 0) {
      waitAndInitialize()
      return
    }

    if (images.length === lazyImages.length && lazyImages.length > 0) {
      const loadedLazyImages = Array.from(lazyImages).filter(checkImageDimensions)
      if (loadedLazyImages.length > 0) {
        waitAndInitialize()
      } else {
        let loadedCount = 0
        const onLazyImageLoad = () => {
          loadedCount++
          if (loadedCount === 1) {
            waitAndInitialize()
          }
        }

        lazyImages.forEach(img => {
          if (checkImageDimensions(img)) {
            onLazyImageLoad()
          } else {
            img.addEventListener('load', onLazyImageLoad, { once: true })
          }
        })

        setTimeout(() => {
          if (!this.flickity) {
            this.createFlickity(mergedOptions)
          }
        }, 1000)
      }
      return
    }

    const firstImage = Array.from(images).find(img => !img.classList.contains('lazy'))

    if (firstImage) {
      if (checkImageDimensions(firstImage)) {
        waitAndInitialize()
      } else {
        const onImageLoad = () => {
          if (checkImageDimensions(firstImage)) {
            waitAndInitialize()
          } else {
            setTimeout(() => {
              if (!this.flickity) {
                this.createFlickity(mergedOptions)
              }
            }, 100)
          }
        }

        firstImage.addEventListener('load', onImageLoad, { once: true })

        setTimeout(() => {
          if (!this.flickity) {
            this.createFlickity(mergedOptions)
          }
        }, 1000)
      }
    } else {
      waitAndInitialize()
    }
  }

  createFlickity(mergedOptions) {
    if (this.flickity) {
      return
    }

    this.flickity = new Flickity(this, mergedOptions)

    const initSmoothScroll = () => {
      if (this.smoothScrollEnabled && !this.animationId) {
        setTimeout(() => {
          this.startSmoothScroll()
          if (this.flickity) {
            this.lastPosition = this.flickity.x
          }
        }, 150)

        if (mergedOptions.pauseAutoPlayOnHover) {
          this.addEventListener('mouseenter', () => this.pauseSmoothScroll())
          this.addEventListener('mouseleave', () => this.resumeSmoothScroll())
        }
      }
    }

    if (this.smoothScrollEnabled) {
      this.flickity.on('change', () => {
        if (this.flickity && this.animationId) {
          const currentPosition = this.flickity.x
          const positionDiff = Math.abs(currentPosition - this.lastPosition)

          if (positionDiff > this.smoothScrollSpeed * 2 || this.isManualNavigation) {
            this.handleManualNavigation()
          }

          this.lastPosition = currentPosition
          this.isManualNavigation = false
        }
      })

      const prevButton = this.querySelector('.flickity-button.previous')
      const nextButton = this.querySelector('.flickity-button.next')

      if (prevButton) {
        prevButton.addEventListener('click', () => {
          this.isManualNavigation = true
        })
      }
      if (nextButton) {
        nextButton.addEventListener('click', () => {
          this.isManualNavigation = true
        })
      }
    }

    this.flickity.on('ready', () => {
      requestAnimationFrame(() => {
        const viewport = this.querySelector('.flickity-viewport')
        if (viewport && viewport.style.height) {
          requestAnimationFrame(() => {
            if (this.style.minHeight) {
              this.style.minHeight = ''
            }
          })
        }
      })

      requestAnimationFrame(() => {
        if (this.flickity) {
          this.flickity.resize()
          const cells = this.flickity.cells
          if (cells && cells.length > 0) {
            requestAnimationFrame(() => {
              if (this.flickity) {
                const cells = this.flickity.cells
                if (cells.length === 1 || (cells.length > 1 && Math.abs(cells[0].x - cells[1].x) > 1)) {
                  this.hideLoader()
                  this.style.opacity = '1'
                  this.style.visibility = 'visible'
                  this.style.transition = 'opacity 0.2s ease-in-out'
                }
              }
            })
          }
        }
      })

      initSmoothScroll()
    })

    const forceResize = () => {
      if (this.flickity) {
        this.flickity.resize()
        const cells = this.flickity.cells
        if (cells && cells.length > 0) {
          const firstCell = cells[0]
          const secondCell = cells[1]
          if (secondCell && Math.abs(firstCell.x - secondCell.x) < 1) {
            requestAnimationFrame(() => {
              if (this.flickity) {
                this.flickity.resize()
                checkAndShow()
              }
            })
            return false
          }
        }
        return true
      }
      return false
    }

    const checkAndShow = () => {
      if (!this.flickity) return

      const cells = this.flickity.cells
      if (cells && cells.length > 1) {
        const firstCell = cells[0]
        const secondCell = cells[1]
        if (secondCell && Math.abs(firstCell.x - secondCell.x) > 1) {
          requestAnimationFrame(() => {
            this.hideLoader()
            this.style.opacity = '1'
            this.style.visibility = 'visible'
            this.style.transition = 'opacity 0.2s ease-in-out'
          })
          return true
        }
      } else if (cells && cells.length === 1) {
        requestAnimationFrame(() => {
          this.hideLoader()
          this.style.opacity = '1'
          this.style.visibility = 'visible'
          this.style.transition = 'opacity 0.2s ease-in-out'
        })
        return true
      }
      return false
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const layoutCorrect = forceResize()
        if (layoutCorrect) {
          checkAndShow()
        }
        setTimeout(() => {
          if (this.flickity) {
            this.flickity.resize()
            checkAndShow()
          }
        }, 100)
      })
    })

    setTimeout(() => {
      if (this.style.opacity === '0') {
        this.hideLoader()
        this.style.opacity = '1'
        this.style.visibility = 'visible'
        this.style.transition = 'opacity 0.2s ease-in-out'
      }
    }, 500)

    if (this.smoothScrollEnabled) {
      setTimeout(() => {
        if (!this.animationId && this.flickity) {
          initSmoothScroll()
        }
      }, 300)
    }
  }

  setupImageLoadListeners() {
    const allImages = this.querySelectorAll('img')

    if (allImages.length === 0) {
      return
    }

    const imageLoadHandler = () => {
      if (this.flickity) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.flickity) {
              this.flickity.resize()
              const cells = this.flickity.cells
              if (cells && cells.length > 1) {
                const firstCell = cells[0]
                const secondCell = cells[1]
                if (secondCell && Math.abs(firstCell.x - secondCell.x) > 1) {
                  this.hideLoader()
                  this.style.opacity = '1'
                  this.style.visibility = 'visible'
                  this.style.transition = 'opacity 0.2s ease-in-out'
                } else if (secondCell && Math.abs(firstCell.x - secondCell.x) < 1) {
                  setTimeout(() => {
                    if (this.flickity) {
                      this.flickity.resize()
                      const cells = this.flickity.cells
                      if (cells && cells.length > 1) {
                        const firstCell = cells[0]
                        const secondCell = cells[1]
                        if (secondCell && Math.abs(firstCell.x - secondCell.x) > 1) {
                          this.hideLoader()
                          this.style.opacity = '1'
                          this.style.visibility = 'visible'
                          this.style.transition = 'opacity 0.2s ease-in-out'
                        }
                      }
                    }
                  }, 50)
                }
              } else if (cells && cells.length === 1) {
                this.hideLoader()
                this.style.opacity = '1'
                this.style.visibility = 'visible'
                this.style.transition = 'opacity 0.2s ease-in-out'
              }
            }
          })
        })
      }
    }

    allImages.forEach(img => {
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        imageLoadHandler()
      } else {
        img.addEventListener('load', imageLoadHandler, { once: true })
        img.addEventListener('error', imageLoadHandler, { once: true })
      }
    })

    if (window.lazyLoadInstance) {
      const checkAndResize = () => {
        if (this.flickity) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this.flickity) {
                this.flickity.resize()
              }
            })
          })
        }
      }

      const observer = new MutationObserver((mutations) => {
        let shouldResize = false
        mutations.forEach(mutation => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            const target = mutation.target
            if (target.tagName === 'IMG' && target.classList.contains('loaded')) {
              shouldResize = true
            }
          }
        })
        if (shouldResize) {
          checkAndResize()
        }
      })

      observer.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      })

      this._imageObserver = observer
    }
  }

  startSmoothScroll() {
    if (this.animationId) {
      this.stopSmoothScroll()
    }

    if (!this.flickity) {
      console.warn('Cannot start smooth scroll: Flickity not initialized')
      return
    }

    const calculateOriginalWidth = () => {
      const cells = this.flickity.cells
      if (!cells || cells.length === 0) return 0

      if (this.flickity.options.wrapAround) {
        let originalCellCount = cells.length
        const cellElements = new Set()

        for (let i = 0; i < cells.length; i++) {
          const cellElement = cells[i].element
          if (cellElements.has(cellElement)) {
            originalCellCount = i
            break
          }
          cellElements.add(cellElement)
        }

        if (originalCellCount === cells.length) {
          const uniqueElements = new Set()
          cells.forEach(cell => uniqueElements.add(cell.element))
          const uniqueCount = uniqueElements.size

          if (uniqueCount <= 3 && cells.length > uniqueCount) {
            originalCellCount = uniqueCount
          } else if (uniqueCount === cells.length && cells.length <= 3) {
            const lastCell = cells[cells.length - 1]
            return lastCell ? lastCell.target + lastCell.size : 0
          }
        }

        const lastOriginalCell = cells[originalCellCount - 1]
        return lastOriginalCell ? lastOriginalCell.target + lastOriginalCell.size : 0
      } else {
        const lastCell = cells[cells.length - 1]
        return lastCell ? lastCell.target + lastCell.size : 0
      }
    }

    let originalWidth = 0
    let attempts = 0
    const maxAttempts = 10

    const animate = () => {
      if (this.isPaused || !this.flickity) {
        this.animationId = requestAnimationFrame(animate)
        return
      }

      const cells = this.flickity.cells
      if (!cells || cells.length === 0) {
        attempts++
        if (attempts < maxAttempts) {
          this.animationId = requestAnimationFrame(animate)
        }
        return
      }

      if (originalWidth === 0) {
        originalWidth = calculateOriginalWidth()
        if (originalWidth === 0) {
          this.animationId = requestAnimationFrame(animate)
          return
        }
      }

      const currentX = this.flickity.x
      let newX = currentX - this.smoothScrollSpeed

      if (this.flickity.options.wrapAround && originalWidth > 0) {
        const cells = this.flickity.cells
        const isSmallCarousel = cells && cells.length <= 6
        const buffer = isSmallCarousel ? this.flickity.size : 0

        if (newX <= -originalWidth - buffer) {
          newX = 0
        }
      } else if (!this.flickity.options.wrapAround) {
        const viewportWidth = this.flickity.size
        const maxScroll = originalWidth > viewportWidth ? -(originalWidth - viewportWidth) : 0

        if (newX < maxScroll) {
          newX = maxScroll
        }
      }

      this.flickity.x = newX
      this.flickity.positionSlider()

      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  stopSmoothScroll() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
  }

  pauseSmoothScroll() {
    this.isPaused = true
  }

  resumeSmoothScroll() {
    this.isPaused = false
  }

  handleManualNavigation() {
    this.pauseSmoothScroll()

    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout)
    }

    this.resumeTimeout = setTimeout(() => {
      this.resumeSmoothScroll()
      this.resumeTimeout = null
    }, 2000)
  }

  observedAttributes = ['carousel-settings']

  get carouselSettings() {
    const carouselSettings = this.getAttribute('carousel-settings')
    if (!carouselSettings) return {}
    const parsedSettings = parseJSON(carouselSettings)
    if (parsedSettings?.groupCells !== undefined) {
      const value = parsedSettings.groupCells

      if (value === 'true') {
        parsedSettings.groupCells = true
      } else if (value === 'false') {
        parsedSettings.groupCells = false
      } else if (!isNaN(value) && typeof value === 'string') {
        parsedSettings.groupCells = Number(value)
      }
    }
    return parsedSettings
  }

  disconnectedCallback() {
    this.stopSmoothScroll()
    if (this.resumeTimeout) {
      clearTimeout(this.resumeTimeout)
      this.resumeTimeout = null
    }
    if (this._imageObserver) {
      this._imageObserver.disconnect()
      this._imageObserver = null
    }
    if (this.flickity) {
      this.flickity.destroy()
    }
  }
}
